'use client';

import React, {
    ButtonHTMLAttributes,
    forwardRef,
    JSXElementConstructor,
} from 'react';
import cn from 'clsx';
import s from './cart-button.module.css';

export type State =
    | 'default'
    | 'selected'
    | 'alternative'
    | 'soldOut'
    | 'added';

export interface CartButtonProps
    extends ButtonHTMLAttributes<HTMLButtonElement> {
    state: State;
    loading?: boolean;
    disabled?: boolean;
    Component?: string | JSXElementConstructor<any>;
    type?: 'submit' | 'reset' | 'button';
    active?: boolean;
    className?: string;
    hrefLink?: string;
    label: string;
    onClick?: (e: any) => void;
    variant?: 'default' | 'upsell';
    title?: string;
}

export const CartButton: React.FC<CartButtonProps> = forwardRef(
    (props, buttonRef) => {
        const {
            state = 'default',
            children,
            loading = false,
            disabled = false,
            Component = 'button',
            active,
            className,
            hrefLink,
            label,
            onClick,
            variant = 'default',
            title,
            ...rest
        } = props;

        const rootClassName = cn(
            s.root,
            {
                [s.upsellRoot]: variant === 'upsell',
                [s.default]: state === 'default',
                [s.selected]: state === 'selected',
                [s.alternative]: state === 'alternative',
                [s.soldOut]: state === 'soldOut',
                [s.added]: state === 'added',
                [s.loading]: loading,
                [s.disabled]: disabled,
            },
            className
        );

        return (
            <>
                {hrefLink ? (
                    <a href={hrefLink} className={s.buttonLink}>
                        <Component
                            title={title}
                            aria-pressed={active}
                            data-variant={state}
                            disabled={disabled}
                            className={rootClassName}
                            {...rest}
                        >
                            {label}
                        </Component>
                    </a>
                ) : (
                    <Component
                        title={title}
                        aria-pressed={active}
                        data-variant={state}
                        disabled={disabled}
                        className={rootClassName}
                        onClick={onClick}
                        {...rest}
                    >
                        {label}
                    </Component>
                )}
            </>
        );
    }
);

CartButton.displayName = 'CartButton';
